import React, { useState, useRef, useEffect } from 'react';

const CATEGORY_TYPE_LABELS = {
  meal_type: 'Meal',
  cuisine: 'Cuisine',
  protein: 'Protein',
  dish_type: 'Dish',
  cooking_method: 'Method',
  diet: 'Diet',
  convenience: 'Style',
  season: 'Season',
  other: 'Other',
};

const CATEGORY_TYPE_ORDER = [
  'meal_type',
  'cuisine',
  'protein',
  'dish_type',
  'cooking_method',
  'diet',
  'convenience',
  'season',
  'other',
];

export default function CategoryFilter({ categories, selected, onChange, onReset, className = '' }) {
  // Group categories by type and sort alphabetically
  const groupedCategories = categories.reduce((acc, cat) => {
    const type = cat.category_type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(cat);
    return acc;
  }, {});

  // Sort each group alphabetically by name
  Object.keys(groupedCategories).forEach((type) => {
    groupedCategories[type].sort((a, b) => a.name.localeCompare(b.name));
  });

  const toggleCategory = (catId) => {
    if (selected.includes(catId)) {
      onChange(selected.filter((id) => id !== catId));
    } else {
      onChange([...selected, catId]);
    }
  };

  const clearAll = () => {
    onChange([]);
  };

  const selectedCount = selected.length;

  // Get types that have categories, in order
  const availableTypes = CATEGORY_TYPE_ORDER.filter((type) => groupedCategories[type]?.length > 0);

  return (
    <div className={`flex flex-wrap gap-2 items-center ${className}`}>
      {availableTypes.map((type) => (
        <CategoryDropdown
          key={type}
          label={CATEGORY_TYPE_LABELS[type]}
          categories={groupedCategories[type]}
          selected={selected}
          onToggle={toggleCategory}
        />
      ))}
      {selectedCount > 0 && (
        <button
          onClick={onReset || clearAll}
          className="px-2 py-1 text-xs text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
        >
          Reset
        </button>
      )}
    </div>
  );
}

function CategoryDropdown({ label, categories, selected, onToggle }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Count selected in this group
  const selectedInGroup = categories.filter((cat) => selected.includes(cat.id));
  const selectedCount = selectedInGroup.length;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
          selectedCount > 0
            ? 'bg-indigo-50 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-600 dark:text-indigo-300'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
        }`}
      >
        {label}
        {selectedCount > 0 && (
          <span className="ml-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-indigo-600 text-white">
            {selectedCount}
          </span>
        )}
        <svg
          className={`w-3 h-3 ml-0.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 max-h-64 overflow-y-auto">
          {categories.map((cat) => {
            const isSelected = selected.includes(cat.id);
            return (
              <label
                key={cat.id}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => onToggle(cat.id)}
                  className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-0"
                />
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: cat.color }}
                />
                <span className="text-xs text-gray-700 dark:text-gray-300 truncate">
                  {cat.name}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
